/**
 * AssignSiteTeamModal Component
 * Modal for assigning Site Manager and Site Supervisor to a site
 */

import React, { useState, useEffect } from 'react';
import { siteDatabaseAPI } from '../../services/api';
import { toast } from 'react-toastify';
import { hasDirectorAccess } from '../../utils/rolePermissions';

const MANAGER_HANDLES_VALUE = '__manager_handles__';

export default function AssignSiteTeamModal({
  isOpen,
  onClose,
  site,
  userRole,
  onSuccess
}) {
  const [siteManagers, setSiteManagers] = useState([]);
  const [siteSupervisors, setSiteSupervisors] = useState([]);
  const [selectedManagerId, setSelectedManagerId] = useState('');
  const [selectedSupervisorId, setSelectedSupervisorId] = useState('');
  const [loading, setLoading] = useState(false);
  const [fetchingManagers, setFetchingManagers] = useState(false);
  const [fetchingSupervisors, setFetchingSupervisors] = useState(false);

  // Check if user can assign managers (director-level, manager, operation_head only)
  const canAssignManager = hasDirectorAccess(userRole) || ['manager', 'operation_head'].includes(userRole);

  // Initialize selected values from site data
  useEffect(() => {
    if (isOpen && site) {
      setSelectedManagerId(site.siteManagerId || site.siteManager?.id || '');
      setSelectedSupervisorId(site.siteSupervisorId || site.siteSupervisor?.id || '');
    }
  }, [isOpen, site]);

  // Fetch site managers when modal opens
  useEffect(() => {
    if (isOpen && canAssignManager) {
      fetchSiteManagers();
    }
  }, [isOpen, canAssignManager]);

  // Fetch site supervisors when manager changes or modal opens
  useEffect(() => {
    if (isOpen) {
      fetchSiteSupervisors(selectedManagerId);
    }
  }, [isOpen, selectedManagerId]);

  const fetchSiteManagers = async () => {
    try {
      setFetchingManagers(true);
      const response = await siteDatabaseAPI.getAvailableSiteManagers();
      setSiteManagers(response.data.data || []);
    } catch (err) {
      console.error('Error fetching site managers:', err);
      toast.error('Failed to load site managers');
    } finally {
      setFetchingManagers(false);
    }
  };

  const fetchSiteSupervisors = async (managerId) => {
    try {
      setFetchingSupervisors(true);
      const response = await siteDatabaseAPI.getAvailableSiteSupervisors(managerId || null);
      setSiteSupervisors(response.data.data || []);
    } catch (err) {
      console.error('Error fetching site supervisors:', err);
      toast.error('Failed to load site supervisors');
    } finally {
      setFetchingSupervisors(false);
    }
  };

  const handleManagerChange = (e) => {
    const newManagerId = e.target.value;
    setSelectedManagerId(newManagerId);
    // Clear supervisor selection when manager changes
    setSelectedSupervisorId('');
  };

  const managerHandlesSelected = selectedSupervisorId === MANAGER_HANDLES_VALUE;

  const handleSave = async () => {
    try {
      setLoading(true);

      if (canAssignManager) {
        const currentManagerId = site.siteManagerId || site.siteManager?.id || '';
        if (selectedManagerId !== currentManagerId) {
          await siteDatabaseAPI.assignSiteManager(site.id, selectedManagerId || null);
        }
      }

      const currentSupervisorId = site.siteSupervisorId || site.siteSupervisor?.id || '';

      if (managerHandlesSelected) {
        await siteDatabaseAPI.assignSiteSupervisor(site.id, null, { managerHandles: true });
      } else if (selectedSupervisorId !== currentSupervisorId) {
        await siteDatabaseAPI.assignSiteSupervisor(site.id, selectedSupervisorId || null);
      }

      toast.success('Site team updated successfully');
      onSuccess?.();
      onClose();
    } catch (err) {
      console.error('Error updating site team:', err);
      toast.error(err.response?.data?.error || 'Failed to update site team');
    } finally {
      setLoading(false);
    }
  };

  const selectedManagerUser = canAssignManager
    ? siteManagers.find((m) => m.id === selectedManagerId)
    : null;
  const selectedManagerLabel =
    selectedManagerUser?.nickname ||
    selectedManagerUser?.fullName ||
    site?.siteManager?.nickname ||
    site?.siteManager?.fullName ||
    site?.siteManagerName ||
    '';
  const canShowManagerHandlesOption = !!selectedManagerId && !!selectedManagerLabel;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={onClose}
        />

        {/* Modal */}
        <div className="relative inline-block bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:max-w-lg sm:w-full">
          {/* Header */}
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Assign Site Team
              </h3>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-500"
              >
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="space-y-4">
              {/* Site Manager Selection */}
              {canAssignManager && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Site Manager
                  </label>
                  <select
                    value={selectedManagerId}
                    onChange={handleManagerChange}
                    disabled={fetchingManagers || loading}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                  >
                    <option value="">-- Select Site Manager --</option>
                    {siteManagers.map((manager) => (
                      <option key={manager.id} value={manager.id}>
                        {manager.nickname || manager.fullName}
                      </option>
                    ))}
                  </select>
                  {fetchingManagers && (
                    <p className="mt-1 text-xs text-gray-500">Loading managers...</p>
                  )}
                </div>
              )}

              {/* Site Manager Display (for site_manager role) */}
              {!canAssignManager && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Site Manager
                  </label>
                  <p className="px-3 py-2 bg-gray-100 rounded-md text-sm text-gray-700">
                    {site.siteManager?.nickname || site.siteManager?.fullName || site.siteManagerName || 'Not Assigned'}
                  </p>
                </div>
              )}

              {/* Site Supervisor Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Site Supervisor
                </label>
                <select
                  value={selectedSupervisorId}
                  onChange={(e) => setSelectedSupervisorId(e.target.value)}
                  disabled={fetchingSupervisors || loading}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                >
                  <option value="">-- Select Site Supervisor --</option>
                  {canShowManagerHandlesOption && (
                    <option value={MANAGER_HANDLES_VALUE}>
                      {selectedManagerLabel} — handle as supervisor
                    </option>
                  )}
                  {siteSupervisors.map((supervisor) => (
                    <option key={supervisor.id} value={supervisor.id}>
                      {supervisor.nickname || supervisor.fullName}
                      {supervisor.siteManager && ` (${supervisor.siteManager.nickname || supervisor.siteManager.fullName})`}
                    </option>
                  ))}
                </select>
                {fetchingSupervisors && (
                  <p className="mt-1 text-xs text-gray-500">Loading supervisors...</p>
                )}
                {!fetchingSupervisors && siteSupervisors.length === 0 && (
                  <p className="mt-1 text-xs text-gray-500">
                    {canAssignManager && selectedManagerId
                      ? 'No supervisors found for this manager'
                      : 'No supervisors available'}
                  </p>
                )}
              </div>

              {/* Info message for site managers */}
              {!canAssignManager && (
                <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                  <p className="text-sm text-blue-700">
                    You can only assign site supervisors from your team.
                  </p>
                </div>
              )}

              {/* Manager-handles-supervision info */}
              {managerHandlesSelected && (
                <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
                  <p className="text-sm text-amber-700">
                    <strong>Manager handles supervision:</strong> No Site Supervisor will be assigned. {selectedManagerLabel} will manage this site directly.
                    {site?.siteStatus === 'pending_ss_assignment' && ' The site will activate on save.'}
                  </p>
                </div>
              )}

              {/* Site activation message when pending SS assignment */}
              {site?.siteStatus === 'pending_ss_assignment' && !managerHandlesSelected && (
                <div className="bg-green-50 border border-green-200 rounded-md p-3">
                  <p className="text-sm text-green-700">
                    <strong>Site Activation:</strong> When you assign a Site Supervisor, the site will automatically become active and phase work can begin.
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse gap-2">
            <button
              type="button"
              onClick={handleSave}
              disabled={loading}
              className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Saving...
                </>
              ) : (
                'Save'
              )}
            </button>
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:w-auto sm:text-sm disabled:opacity-50"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
